import { useId } from 'react';

import styles from './ReviewsSection.module.css';

export type Review = {
  reviewDate?: string;
  reviewHeading?: string;
  reviewStarRating: number;
  reviewText: string;
  reviewerName?: string;
};

export type ReviewsSectionProps = {
  heading?: string;
  reviews?: Review[] | null;
};

type StarFill = 'full' | 'half' | 'empty';

const computeStarCells = (value: number): StarFill[] => {
  const clamped = Math.max(0, Math.min(5, value));
  const snapped = Math.round(clamped * 2) / 2;
  const fullCount = Math.floor(snapped);
  const hasHalf = snapped % 1 !== 0;

  return Array.from({ length: 5 }, (_, i) => {
    if (i < fullCount) return 'full';
    if (i === fullCount && hasHalf) return 'half';
    return 'empty';
  });
};

const Star = ({ fill }: { fill: StarFill }) => {
  const gradId = useId();
  const pathFill =
    fill === 'full'
      ? 'var(--reviews-star-color-full)'
      : fill === 'half'
        ? `url(#${gradId})`
        : 'var(--reviews-star-color-empty)';
  return (
    <svg aria-hidden="true" className={styles.star} data-fill={fill} height={19} viewBox="0 0 20 19" width={20}>
      {fill === 'half' && (
        <defs>
          <linearGradient id={gradId} x1="0" x2="1" y1="0" y2="0">
            <stop offset="50%" stopColor="var(--reviews-star-color-full)" />
            <stop offset="50%" stopColor="var(--reviews-star-color-empty)" />
          </linearGradient>
        </defs>
      )}
      <path
        d="M9.07088 0.612343C9.41462 -0.204115 10.5854 -0.204114 10.9291 0.612346L12.9579 5.43123C13.1029 5.77543 13.4306 6.01061 13.8067 6.0404L19.0727 6.45748C19.9649 6.52814 20.3267 7.62813 19.6469 8.2034L15.6348 11.5987C15.3482 11.8412 15.223 12.2218 15.3106 12.5843L16.5363 17.661C16.744 18.5211 15.7969 19.201 15.033 18.7401L10.5245 16.0196C10.2025 15.8252 9.7975 15.8252 9.47548 16.0196L4.96699 18.7401C4.20311 19.201 3.25596 18.5211 3.46363 17.661L4.68942 12.5843C4.77698 12.2218 4.65182 11.8412 4.36526 11.5987L0.353062 8.2034C-0.326718 7.62813 0.0350679 6.52814 0.927291 6.45748L6.19336 6.0404C6.5695 6.01061 6.89716 5.77543 7.04207 5.43123L9.07088 0.612343Z"
        fill={pathFill}
      />
    </svg>
  );
};

const StarRating = ({ value }: { value: number }) => {
  const cells = computeStarCells(value);
  return (
    <span aria-label={`Rated ${Math.max(0, Math.min(5, value))} out of 5 stars`} className={styles.stars} role="img">
      {cells.map((fill, i) => (
        <Star fill={fill} key={i} />
      ))}
    </span>
  );
};

const ReviewCard = ({ review }: { review: Review }) => {
  const meta = [review.reviewerName, review.reviewDate].filter(Boolean).join(', ');
  return (
    <li className={styles.card}>
      <div className={styles.cardTop}>
        {review.reviewHeading && <h3 className={styles.cardHeading}>{review.reviewHeading}</h3>}
        <StarRating value={review.reviewStarRating} />
      </div>
      <p className={styles.text}>{review.reviewText}</p>
      {meta && (
        <p className={styles.meta} data-testid="review-meta">
          {meta}
        </p>
      )}
    </li>
  );
};

export const ReviewsSection = ({ heading, reviews }: ReviewsSectionProps) => {
  const items = reviews ?? [];
  return (
    <section className={styles.section}>
      {heading && <h2 className={styles.heading}>{heading}</h2>}
      {items.length > 0 && (
        <ul className={styles.list}>
          {items.map((review, i) => (
            <ReviewCard key={i} review={review} />
          ))}
        </ul>
      )}
    </section>
  );
};
